import { useState } from 'react'
import { Select, toast } from '@/components/ui'
import { useRecordDecision } from '../api/queries'
import type { Decision, PriorDecision } from '../api/types'

const DECISION_LABEL: Record<Decision, string> = {
  buy: 'Buy',
  ask_customer: 'Ask customer',
  hold: 'Hold',
  ignore: 'Ignore',
}

/**
 * Autosaving decision picker. Local state is seeded once from the
 * carried-forward decision and never re-keyed on refetch, so a refresh
 * can't wipe a choice mid-edit.
 */
export function DecisionSelect({
  productId,
  runId,
  prior,
}: {
  productId: number
  runId: string | undefined
  prior: PriorDecision | undefined
}) {
  const [value, setValue] = useState<Decision | ''>(prior?.decision ?? '')
  const record = useRecordDecision()

  return (
    <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
      <Select
        aria-label="Decision"
        value={value}
        disabled={record.isPending}
        onChange={(e) => {
          const next = e.target.value as Decision | ''
          if (!next) return
          const previous = value
          setValue(next)
          record.mutate(
            { product_id: productId, decision: next, run_id: runId ?? null },
            {
              onError: (err) => {
                setValue(previous)
                toast.danger(`Decision not saved${err instanceof Error ? `: ${err.message}` : ''}`)
              },
            },
          )
        }}
      >
        <option value="">Decide…</option>
        {(Object.keys(DECISION_LABEL) as Decision[]).map((d) => (
          <option key={d} value={d}>
            {DECISION_LABEL[d]}
          </option>
        ))}
      </Select>
      {prior?.status === 'changed since decision' ? (
        <span className="text-role-metadata text-warning-text">Changed since decision</span>
      ) : null}
    </div>
  )
}
